import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronRight } from '@mui/icons-material';
import { getAllDevelopmentData } from '../../api';
import SlidingAiPanel from '../../components/SlidingAiPanel';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  Filler
} from 'chart.js';

// Import the types
import { Developer, Project } from '../../types/DeveloperTypes';

// Import the components
import DeveloperTrumpCard from '../../components/DeveloperTrumpCard';
import './DevelopmentDevelopers.css';
import DeveloperDetailCard from '../../components/DeveloperDetailCard';
import ProjectSelector from '../../components/ProjectSelector';
import { useProjectSelection } from '../../context/ProjectSelectionContext';
import NoDataPlaceholder from '../../components/NoDataPlaceholder';
import { format } from 'date-fns';
import LoadingSpinner from '../../components/LoadingSpinner';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

const DevelopmentDevelopers: React.FC = () => {
  const location = useLocation();

  // --- FLIP Animation State ---
  const [floatingCard, setFloatingCard] = useState<{
    idx: number;
    top: number;
    left: number;
    width: number;
    height: number;
    animating: boolean;
    containerTop?: number;
  } | null>(null);
  // To directly access card DOM nodes
  const cardDomRefs = useRef<Array<HTMLDivElement | null>>([]);

  // --- FLIP Animation Handlers ---
  const handleCardClick = (
    e: React.MouseEvent<HTMLDivElement>,
    idx: number,
    developer: Developer
  ) => {
    // If already animating, ignore
    if (floatingCard) return;
    // If already active, just set
    if (activeCardIndex === idx) {
      setActiveCardIndex(idx);
      setSelectedDeveloper(developer);
      return;
    }

    // Prefer measuring the actual card element inside the wrapper; fallback to wrapper ref
    let cardNode = cardDomRefs.current[idx] || e.currentTarget as HTMLDivElement;
    let rect = cardNode?.getBoundingClientRect();
    if (cardNode && rect?.height === 0) {
      cardNode = e.currentTarget as HTMLDivElement;
      rect = cardNode?.getBoundingClientRect();
    }
    //const innerCardNode = wrapperNode?.firstElementChild as HTMLElement | null;
    //const cardNode = innerCardNode || (cardDomRefs.current[idx] as HTMLElement | null) || wrapperNode;

    // Get DOM node for the clicked card
    //const cardNode = cardDomRefs.current[idx];
    if (!cardNode) {
      setActiveCardIndex(idx);
      setSelectedDeveloper(developer);
      setAiSummary(developer.AISummary || null);
      return;
    }





    // Mobile-specific animation - center the card in viewport
    if (isMobile) {
      const containerTopOffset = (window.innerHeight * 0.5) - (rect.height * 0.5) - 15;
      const targetLeft = (window.innerWidth * 0.5) - (rect.width * 0.5) - 11;

      setFloatingCard({
        idx,
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        animating: false,
        containerTop: rect.top,
      });

      requestAnimationFrame(() => {
        setFloatingCard(fc => fc && {
          ...fc,
          top: containerTopOffset,
          left: targetLeft,
          width: rect.width,
          height: rect.height,
          animating: true,
          containerTop: containerTopOffset,
        });
      });
    } else {
      // Desktop animation (unchanged)
      const containerTopOffest = (window.innerHeight * selectedCardWindowYPosition) - rect.height + 65;
      const targetLeft = rect.left + 73;

      setFloatingCard({
        idx,
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        animating: false,
        containerTop: rect.top,
      });

      requestAnimationFrame(() => {
        setFloatingCard(fc => fc && {
          ...fc,
          top: containerTopOffest,
          left: targetLeft,
          width: rect.width,
          height: rect.height,
          animating: true,
          containerTop: containerTopOffest,
        });
      });
    }
  };

  const handleFloatingCardTransitionEnd = () => {
    if (floatingCard) {
      // Set the active card index to show the card in the correct position
      setActiveCardIndex(floatingCard.idx);
      setSelectedDeveloper(searchFilteredDevelopers[floatingCard.idx]);

      // Clear the floating card after setting the active card
      setFloatingCard(null);
    }
  };

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const { selectedProjectId, setSelectedProjectId } = useProjectSelection();
  const [selectedDeveloper, setSelectedDeveloper] = useState<Developer | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [aiPanelOpen, setAiPanelOpen] = useState<boolean>(false);
  const [aiSummary, setAiSummary] = useState<{ summary: string; createdOn: string } | null>(null);
  const [aiAnswer, setAiAnswer] = useState<string>('');
  const [activeCardIndex, setActiveCardIndex] = useState<number | null>(null);
  const [aiLoading, setAiLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const cardRefs = useRef<Array<React.RefObject<{ flipToFront: () => boolean } | null>>>([]);
  const cardDeckTopOffset = 20;
  const selectedCardWindowYPosition = 0.6;

  // Mobile/tablet responsive states
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Add a state variable for currentProjectSummary
  const [currentProjectSummary, setCurrentProjectSummary] = useState<{ summary: string; createdOn: string } | null>(null);

  // Update useEffect to set currentProjectSummary when selectedProjectId changes
  useEffect(() => {
    if (selectedProjectId) {
      const project = projects.find(p => p.projectId === selectedProjectId);
      setCurrentProjectSummary(project?.developerAISummary || null);
    }
  }, [selectedProjectId, projects]);

  // Handle responsive breakpoints
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setWindowWidth(width);
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);

      // Close mobile drawer when switching to desktop
      if (width >= 1024) {
        setMobileDrawerOpen(false);
      }
    };

    handleResize(); // Set initial state
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle body scroll prevention when mobile drawer is open
  useEffect(() => {
    if (isMobile) {
      if (mobileDrawerOpen) {
        // Store current scroll position
        const scrollY = window.scrollY;
        document.body.style.top = `-${scrollY}px`;
        document.body.classList.add('mobile-drawer-open');
      } else {
        // Restore scroll position
        const scrollY = document.body.style.top;
        document.body.classList.remove('mobile-drawer-open');
        document.body.style.top = '';
        if (scrollY) {
          window.scrollTo(0, parseInt(scrollY || '0') * -1);
        }
      }
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('mobile-drawer-open');
      document.body.style.top = '';
    };
  }, [mobileDrawerOpen, isMobile]);

  // Fetch developers data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getAllDevelopmentData();
        console.log('Developers Data Response:', response);

        if (response && response.projects && Array.isArray(response.projects)) {
          setProjects(response.projects);

          // Set default selected project if not already set
          if (!selectedProjectId && response.projects.length > 0) {
            setSelectedProjectId(response.projects[0].projectId);
            setSelectedProject(response.projects[0]);
          }
          //if selectedproject is null, set it          
          if (!selectedProject) {
            setSelectedProject(response.projects[0]);
          }
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching developers data:', error);
        setError('Failed to load developers data. Please try again later.');
        setLoading(false);
      }
    };

    fetchData();
  }, []);



  // Filter developers for the selected project
  const filteredDevelopers = useMemo(() => {
    if (!projects.length) return [];

    // Always find and set the selected project based on selectedProjectId
    const sProject = projects.find(p => p.projectId === selectedProjectId);

    if (sProject) {
      setSelectedProject(sProject);
      return (sProject.Developers || []).sort((a, b) => (b.ProductivityAverage || 0) - (a.ProductivityAverage || 0));
    } else if (projects.length > 0) {
      // Fallback to first project if selected project not found
      const fallbackProject = projects[0];
      setSelectedProjectId(fallbackProject.projectId);
      setSelectedProject(fallbackProject);
      return (fallbackProject.Developers || []).sort((a, b) => (b.ProductivityAverage || 0) - (a.ProductivityAverage || 0));
    }

    return [];
  }, [selectedProjectId, projects, setSelectedProjectId]);

  // Initialize card refs when filtered developers change
  useEffect(() => {
    cardRefs.current = filteredDevelopers.map(() => React.createRef());
  }, [filteredDevelopers]);

  // Handle preselection from navigation state
  useEffect(() => {
    const state = location.state as { selectedDeveloperName?: string; selectedProjectName?: string } | null;
    if (state && projects.length > 0) {
      // Find and select the project if specified
      if (state.selectedProjectName) {
        const project = projects.find(p => p.projectName === state.selectedProjectName);
        if (project) {
          setSelectedProjectId(project.projectId);
          setSelectedProject(project);
        }
      }

      // Find and select the developer if specified
      if (state.selectedDeveloperName) {
        // Wait for the next tick to ensure filteredDevelopers is updated
        setTimeout(() => {
          const currentProject = selectedProject || projects.find(p => p.projectName === state.selectedProjectName) || projects[0];
          const developer = currentProject?.Developers?.find(d => d.DeveloperName === state.selectedDeveloperName);
          if (developer) {
            setSelectedDeveloper(developer);
            setAiSummary(developer.AISummary || null);
            // Find the index of the developer in the filtered list
            const developerIndex = (currentProject?.Developers || []).findIndex(d => d.DeveloperName === state.selectedDeveloperName);
            if (developerIndex !== -1) {
              setActiveCardIndex(developerIndex);
            }
          }
        }, 100);
      }

      // Clear the navigation state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [projects, location.state, selectedProject]);

  // Apply search filter to developers
  const searchFilteredDevelopers = useMemo(() => {
    if (!searchQuery.trim()) return filteredDevelopers;

    const query = searchQuery.toLowerCase().trim();
    return filteredDevelopers.filter(developer =>
      developer.DeveloperName.toLowerCase().includes(query)
    );
  }, [filteredDevelopers, searchQuery]);

  let dataSnapshotRange = ``;
  if (selectedProject) {
    dataSnapshotRange = `${format(new Date(selectedProject.snapshotStartDate), 'do MMMM yyyy HH:mm')} 
    - ${format(new Date(selectedProject.summaryDate), 'do MMMM yyyy HH:mm')}`;
  }

  // Add a callback to update selectedDeveloper.AISummary
  const updateDeveloperAISummary = (summary: string, createdOn: string) => {
    if (selectedDeveloper) {
      const aiSummaryObject = { summary, createdOn };
      setSelectedDeveloper(prev => prev ? { ...prev, AISummary: aiSummaryObject } : null);
      setProjects(prevProjects => {
        return prevProjects.map(project => {
          if (project.projectId === selectedProjectId) {
            return {
              ...project,
              Developers: project.Developers.map(dev => {
                if (dev.DeveloperId === selectedDeveloper.DeveloperId) {
                  return { ...dev, AISummary: aiSummaryObject };
                }
                return dev;
              })
            };
          }
          return project;
        });
      });
    }
  };

  if (loading) {
    return <LoadingSpinner title="Loading developers data..." />;
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#f44336' }}>
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (selectedProject?.Developers?.length === 0) {
    return (
      <NoDataPlaceholder title="Developer trend analysis not available" message="There needs to be at least 2 iterations/sprints to be able to display trend performance for developers" />
    );
  }

  return (
    <div style={{
      //padding: isMobile ? '1rem' : '0 1rem',
      paddingLeft: '1rem',
      paddingRight: '1rem',
      paddingBottom: '2rem',
      paddingTop: '1rem',
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      boxSizing: 'border-box',
      minWidth: 0
    }}>
      {/* Mobile fixed top project selector */}
      {isMobile && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 999,
            paddingTop: 40,
            background: '#ffffff',
            borderBottom: '1px solid #e5e7eb',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}
        >
          <div style={{ maxWidth: 1400, margin: '0 auto', padding: '12px 16px' }}>
            <div style={{
              fontSize: 20,
              fontWeight: 300,
              color: '#374151',
              marginBottom: 8,
              textAlign: 'center',
              fontFamily: 'Raleway, sans-serif'
            }}>
              Developers Overview
            </div>
            {projects && projects.length > 0 && (
              <select
                value={selectedProjectId || ''}
                onChange={(e) => setSelectedProjectId(Number(e.target.value))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  background: '#f9fafb',
                  fontSize: 14,
                  color: '#111827'
                }}
              >
                {projects.map((project) => (
                  <option key={project.projectId} value={project.projectId}>
                    {project.projectName}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}

      {/* Spacer for mobile fixed header */}
      {isMobile && <div style={{ height: '50px' }} />}

      {/* Header with title and project selector */}
      {!isMobile && (
        <div className="developer-header-container" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexDirection: 'row',
          gap: '1rem'
        }}>
          <h1 style={{
            margin: 0,
            fontSize: 'clamp(1.5rem, 4vw, 2rem)',
            lineHeight: '1.2',
            wordBreak: 'break-word',
            maxWidth: '100%',
            overflow: 'hidden',
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 300,
          }}>{`Developers Overview (${selectedProject?.projectName})`}</h1>

          <ProjectSelector
            projects={projects}
            selectedProjectId={selectedProjectId}
            onSelectProject={setSelectedProjectId}
          />
        </div>
      )}
      <div>
        {selectedProject?.summaryDate && (
          <p className="dashboard-subtitle" style={{ marginBottom: '2rem', paddingLeft: `${isMobile ? 'clamp(1rem, 4vw, 2rem)' : '0rem'}`, paddingRight: '2rem', color: '#666', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
            Data snapshot: {dataSnapshotRange}
          </p>
        )}
      </div>

      {/* Main Content */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>

        {/* New layout container for cards and details */}
        <div className={`developers-layout-container ${isMobile ? 'mobile' : 'desktop'}`}>
          {/* Mobile: Active and Floating card layers rendered globally above the drawer */}
          {isMobile && !floatingCard && activeCardIndex !== null && (
            <div
              className={`deck-card-wrapper ${mobileDrawerOpen ? 'mobile-deck-card-active' : 'mobile-deck-card-active-hidden'}`}
              style={{ zIndex: 10000, left: 10 }}
              ref={el => { if (activeCardIndex !== null) cardDomRefs.current[activeCardIndex] = el; }}
            >
              <DeveloperTrumpCard
                developer={searchFilteredDevelopers[activeCardIndex]}
                isTopPerformer={searchFilteredDevelopers[activeCardIndex].IsTopPerformer}
                aiSummary={searchFilteredDevelopers[activeCardIndex].AISummary?.summary}
                active={true}
                cardRef={activeCardIndex !== null ? cardRefs.current[activeCardIndex] : undefined}
                onClick={() => { }}
                onCloseActive={() => {
                  setActiveCardIndex(null);
                  setSelectedDeveloper(null);
                }}
                isMobile={true}
                onMobileDataClick={() => {
                  // flip to front if needed
                  if (activeCardIndex !== null) {
                    try {
                      cardRefs.current[activeCardIndex]?.current?.flipToFront?.();
                    } catch { /* noop */ }
                  }
                  setMobileDrawerOpen(false);
                  // Keep developer selected to show data on main screen
                }}
              />
            </div>
          )}

          {isMobile && floatingCard && (
            <div
              className="deck-card-wrapper mobile-floating-card"
              style={{
                position: 'fixed',
                zIndex: 10000,
                top: floatingCard.top,
                left: floatingCard.left,
                width: floatingCard.width,
                height: floatingCard.height,
                transition: floatingCard.animating ? 'all 0.5s cubic-bezier(.4,1.3,.6,1)' : 'none',
                transform: floatingCard.animating ? 'scale(1.08) translateY(0)' : 'none',
                pointerEvents: 'none'
              }}
              onTransitionEnd={handleFloatingCardTransitionEnd}
            >
              <DeveloperTrumpCard
                developer={searchFilteredDevelopers[floatingCard.idx]}
                isTopPerformer={searchFilteredDevelopers[floatingCard.idx].IsTopPerformer}
                aiSummary={searchFilteredDevelopers[floatingCard.idx].AISummary?.summary}
                active={true}
                cardRef={undefined}
                onClick={() => { }}
                onCloseActive={() => { }}
                isMobile={true}
                onMobileDataClick={() => {
                  setMobileDrawerOpen(false);
                  // Keep developer selected to show data on main screen
                }}
              />
            </div>
          )}
          {/* Mobile drawer for cards */}
          {windowWidth < 768 && (
            <>
              {/* Mobile drawer overlay */}
              {mobileDrawerOpen && (
                <div
                  className="mobile-drawer-overlay"
                  onClick={() => setMobileDrawerOpen(false)}
                />
              )}

              {/* Mobile drawer - replicating desktop card stack */}
              <div className={`mobile-cards-drawer ${mobileDrawerOpen ? 'open' : ''}`}>
                {/* Developer Search */}
                <div style={{ marginBottom: '0.5rem', position: 'relative', zIndex: 2000 }}>
                  <input
                    type="text"
                    placeholder="Search developers..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      width: '250px',
                      fontSize: '14px'
                    }}
                  />
                </div>



                {/* Mobile Developer Cards - Stacked like desktop */}
                <div className="mobile-deck-of-cards-container">
                  {searchFilteredDevelopers.length > 0 ? (
                    searchFilteredDevelopers.map((developer, idx) => (
                      activeCardIndex !== idx ? (
                        <div
                          key={developer.DeveloperId}
                          className="mobile-deck-card-wrapper"
                          style={{
                            zIndex: 1100 + idx,
                            top: `${(idx * 48) + cardDeckTopOffset}px`,
                            visibility: floatingCard && floatingCard.idx === idx ? 'hidden' : undefined,
                          }}
                          ref={el => { cardDomRefs.current[idx] = el; }}
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleCardClick(e, idx, developer);
                          }}
                        >
                          <DeveloperTrumpCard
                            developer={developer}
                            isTopPerformer={developer.IsTopPerformer}
                            aiSummary={developer.AISummary?.summary}
                            active={false}
                            cardRef={cardRefs.current[idx]}
                            onClick={() => { }}
                            onCloseActive={() => {
                              setActiveCardIndex(null);
                              setSelectedDeveloper(null);
                            }}
                          />
                        </div>
                      ) : <React.Fragment key={developer.DeveloperId} />
                    ))
                  ) : searchQuery && filteredDevelopers.length > 0 ? (
                    <p style={{ position: 'relative', zIndex: 2000, padding: '20px' }}>No developers found matching "{searchQuery}".</p>
                  ) : (
                    <p style={{ position: 'relative', zIndex: 2000, padding: '20px' }}>No developer trend data available for this project.</p>
                  )}
                </div>
              </div>

              {/* Mobile drawer handle */}
              <div
                className="mobile-drawer-handle"
                onClick={() => setMobileDrawerOpen(!mobileDrawerOpen)}
              >
                <div className="handle-icon">
                  <ChevronRight />
                </div>
                <span className="handle-text">Choose a Developer</span>
              </div>
            </>
          )}

          {/* Desktop cards container */}
          <div className={`deck-of-cards-container-wrapper ${windowWidth < 768 ? 'mobile-hidden' : ''}`}>
            {/* Developer Search */}
            <div style={{ marginBottom: '0.5rem' }}>
              <input
                type="text"
                placeholder="Search developers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  width: '250px',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* <div className="deck-of-cards-title">
              <h3>Recent Performance</h3>
            </div> */}

            {/* Active card - moved to parent container */}
            {!floatingCard && activeCardIndex !== null && (
              <div
                className={`deck-card-wrapper ${isMobile ? 'mobile-deck-card-active' : 'deck-card-active'}`}
                style={{
                  zIndex: 9999,
                  ...(isMobile ? {} : {
                    position: 'absolute',
                    top: `${(window.innerHeight * selectedCardWindowYPosition) - (cardDomRefs.current[activeCardIndex] ? cardDomRefs.current[activeCardIndex]!.offsetHeight : 0) + 54}px`,
                    transition: 'none',
                  })
                }}
                ref={el => { if (activeCardIndex !== null) cardDomRefs.current[activeCardIndex] = el; }}
                onClick={e => {
                  if (activeCardIndex !== null) {
                    handleCardClick(e, activeCardIndex, searchFilteredDevelopers[activeCardIndex]);
                  }
                }}
              >
                <DeveloperTrumpCard
                  developer={searchFilteredDevelopers[activeCardIndex]}
                  isTopPerformer={searchFilteredDevelopers[activeCardIndex].IsTopPerformer}
                  aiSummary={searchFilteredDevelopers[activeCardIndex].AISummary?.summary}
                  active={true}
                  cardRef={activeCardIndex !== null ? cardRefs.current[activeCardIndex] : undefined}
                  onClick={() => { }}
                  onCloseActive={() => {
                    setActiveCardIndex(null);
                    setSelectedDeveloper(null);
                  }}
                  isMobile={isMobile}
                  onMobileDataClick={() => {
                    // flip to front if needed
                    if (activeCardIndex !== null) {
                      try {
                        cardRefs.current[activeCardIndex]?.current?.flipToFront?.();
                      } catch { /* noop */ }
                    }
                    setMobileDrawerOpen(false);
                    // Keep developer selected to show data on main screen
                  }}
                />
              </div>
            )}

            {/* Floating card for animation - moved to parent container */}
            {floatingCard && (
              <div
                className="deck-card-wrapper floating-card"
                style={{
                  position: 'fixed',
                  zIndex: 9999,
                  top: floatingCard.top,
                  left: floatingCard.left,
                  width: floatingCard.width,
                  height: floatingCard.height,
                  transition: floatingCard.animating ? 'all 0.5s cubic-bezier(.4,1.3,.6,1)' : 'none',
                  transform: floatingCard.animating ? 'scale(1.08) translateY(0)' : 'none',
                  pointerEvents: 'none'
                }}
                onTransitionEnd={handleFloatingCardTransitionEnd}
              >
                <DeveloperTrumpCard
                  developer={searchFilteredDevelopers[floatingCard.idx]}
                  isTopPerformer={searchFilteredDevelopers[floatingCard.idx].IsTopPerformer}
                  aiSummary={searchFilteredDevelopers[floatingCard.idx].AISummary?.summary}
                  active={true}
                  cardRef={undefined}
                  onClick={() => { }}
                  onCloseActive={() => { }}
                />
              </div>
            )}

            {/* Developer Cards - Stacked Vertically */}
            <div className="deck-of-cards-container">
              {searchFilteredDevelopers.length > 0 ? (
                searchFilteredDevelopers.map((developer, idx) => (
                  <React.Fragment key={developer.DeveloperId}>
                    {/* Regular cards - only show if not active */}
                    {activeCardIndex !== idx && (
                      <div
                        className={`deck-card-wrapper`}
                        style={{
                          zIndex: 100 + idx,
                          top: `${activeCardIndex !== null && idx > activeCardIndex ? ((idx - 1) * 48 + 48) + cardDeckTopOffset : (idx * 48) + cardDeckTopOffset}px`,//cards below the selected one
                          visibility: floatingCard && floatingCard.idx === idx ? 'hidden' : undefined
                        }}
                        ref={el => { cardDomRefs.current[idx] = el; }}
                        onClick={e => handleCardClick(e, idx, developer)}
                      >
                        <DeveloperTrumpCard
                          developer={developer}
                          isTopPerformer={developer.IsTopPerformer}
                          aiSummary={developer.AISummary?.summary}
                          active={false}
                          cardRef={cardRefs.current[idx]}
                          onClick={() => {
                            if (activeCardIndex !== idx) {
                              setActiveCardIndex(idx);
                              setSelectedDeveloper(developer);
                            }
                          }}
                          onCloseActive={() => {
                            setActiveCardIndex(null);
                            setSelectedDeveloper(null);
                          }}
                        />
                      </div>
                    )}
                    {/* Floating card removed from here - now in parent container */}
                  </React.Fragment>
                ))
              ) : searchQuery && filteredDevelopers.length > 0 ? (
                <p>No developers found matching "{searchQuery}".</p>
              ) : (
                <p>No developer trend data available for this project.</p>
              )}
            </div>
          </div>

          {/* Developer Detail View */}
          <div className="developer-details-section">
            {selectedDeveloper ? (
              <div>
                <DeveloperDetailCard
                  developer={selectedDeveloper}
                  onClose={() => setSelectedDeveloper(null)}
                  projectId={selectedProjectId || undefined}
                />
              </div>
            ) : (
              <DeveloperDetailCard
                developer={null}
                projectId={selectedProjectId || undefined}
                teamData={{
                  recentIterations: projects.find(p => p.projectId === selectedProjectId)?.recentIterations || [],
                  developers: filteredDevelopers.map(dev => ({
                    ...dev,
                    IterationData: dev.IterationData?.map(iter => ({
                      ...iter,
                      ItemsOverEstimate: iter.ItemsOverEstimate || 0,
                      ItemsUnderEstimate: iter.ItemsUnderEstimate || 0,
                      TopOverEstimateItems: iter.TopOverEstimateItems || [],
                      WorkItemTasksAssigned: iter.WorkItemTasksAssigned || 0
                    })) || []
                  })),
                  metrics: {
                    openBugs: projects.find(p => p.projectId === selectedProjectId)?.metrics?.openBugs || {
                      value: 0,
                      trend: 0,
                      previousValue: 0,
                      isGoodTrend: false
                    },
                    avgPRReviewTime: projects.find(p => p.projectId === selectedProjectId)?.metrics?.avgPRReviewTime || {
                      value: 0,
                      trend: 0,
                      previousValue: 0,
                      difference: 0,
                      isGoodTrend: false
                    },
                    completedStories: projects.find(p => p.projectId === selectedProjectId)?.metrics?.completedStories || {
                      value: 0,
                      trend: 0,
                      previousValue: 0,
                      isGoodTrend: false
                    },
                    sprintGoalAchievement: projects.find(p => p.projectId === selectedProjectId)?.metrics?.sprintGoalAchievement || {
                      value: 0,
                      trend: 0,
                      previousValue: 0,
                      isGoodTrend: false
                    },
                    velocityPercentage: projects.find(p => p.projectId === selectedProjectId)?.metrics?.velocityPercentage || {
                      value: 0,
                      trend: 0,
                      previousValue: 0,
                      isGoodTrend: false
                    }
                  }
                }}
                onClose={() => setSelectedDeveloper(null)}
              />
            )}
          </div>
        </div>
      </div>

      {/* AI Panel for insights and follow-up questions */}
      {(() => {
        const canGenerateAiSummary = (aiGeneratedDate: string | undefined): boolean => {
          return aiGeneratedDate && selectedProject?.summaryDate ? new Date(aiGeneratedDate) < new Date(selectedProject.summaryDate) : true;
        };

        return (
          <SlidingAiPanel
            insight={selectedDeveloper ? (selectedDeveloper.AISummary?.summary || '') : (currentProjectSummary?.summary || '')}
            summaryCreatedOn={selectedDeveloper ? selectedDeveloper.AISummary?.createdOn : currentProjectSummary?.createdOn}
            canGenerateNewSummary={canGenerateAiSummary(selectedDeveloper ? selectedDeveloper.AISummary?.createdOn : currentProjectSummary?.createdOn)}
            contextType={selectedDeveloper ? "developer" : "project-developer"}
            entityName={selectedDeveloper ? selectedDeveloper.DeveloperName : "Development Team Insights"}
            entityId={selectedDeveloper ? selectedDeveloper.DeveloperId : selectedProjectId?.toString() || ''}
            projectId={selectedProjectId?.toString()}
            initialOpen={aiPanelOpen}
            shouldCloseOnOutisdeClick={false}
            onStreamedContentChange={updateDeveloperAISummary}
            buttonTitle={selectedDeveloper ? "Exec Developer Insights" : "Exec Team Insights"}
          />
        );
      })()}
    </div>
  );
};

export default DevelopmentDevelopers;